import { useState, useEffect } from 'react';
import { motion, AnimatePresence} from 'framer-motion';

const navItems = [
  { id: 'home', label: 'Home', icon: '🏠' },
  { id: 'about', label: 'About', icon: '👤' },
  { id: 'experience', label: 'Experience', icon: '💼' },
  { id: 'education', label: 'Education', icon: '🎓' },
  { id: 'skills', label: 'Skills', icon: '⚡' },
  { id: 'projects', label: 'Projects', icon: '💻' },
  { id: 'contact', label: 'Contact', icon: '📧' }
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [menuOpen, setMenuOpen] = useState(false);
  // Header should always stay visible while scrolling

  /* Scroll detection (no hide-on-scroll, only style change) */
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 20);
    };
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /* Active section detection */
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { rootMargin: '-40% 0px -40% 0px' }
    );

    navItems.forEach(item => {
      const el = document.getElementById(item.id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const scrollToSection = id => {
    const el = document.getElementById(id);
    if (!el) return;
    const offset = 90;
    const y = el.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top: y, behavior: 'smooth' });
    setMenuOpen(false);
  };

  return (
    <>
      <style>{css}</style>

      <motion.header
        className={`header ${scrolled ? 'scrolled' : ''}`}
        animate={{ y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div className="header-container">
          <motion.div className="logo" whileHover={{ scale: 1.05 }}>
            <a href="#home" onClick={e => { e.preventDefault(); scrollToSection('home'); }}>
              <span className="logo-bracket">&lt;</span>
              <span className="logo-text">Divita</span>
              <span className="logo-bracket">/&gt;</span>
            </a>
          </motion.div>

          <nav className="nav-desktop">
            {navItems.map((item, i) => (
              <motion.a
                key={item.id}
                href={`#${item.id}`}
                className={`nav-link ${activeSection === item.id ? 'active' : ''}`}
                onClick={e => { e.preventDefault(); scrollToSection(item.id); }}
                whileHover={{ scale: 1.1, textShadow: '0 0 12px rgba(0,255,65,0.8)' }}
                whileTap={{ scale: 0.95 }}
              >
                {item.label}
                {activeSection === item.id && (
                  <motion.span layoutId="indicator" className="nav-indicator" />
                )}
              </motion.a>
            ))}
          </nav>

          <button className="menu-toggle" onClick={() => setMenuOpen(!menuOpen)}>
            <span /><span /><span />
          </button>
        </div>

        {/* <motion.div className="progress-bar" style={{ scaleX }} /> */}
      </motion.header>

      <AnimatePresence>
        {menuOpen && (
          <>
            <motion.div className="mobile-overlay" onClick={() => setMenuOpen(false)} />
            <motion.nav
              className="mobile-menu"
              initial={{ x: '100%', rotateY: -10 }}
              animate={{ x: 0, rotateY: 0 }}
              exit={{ x: '100%', rotateY: 10 }}
            >
              {navItems.map(item => (
                <button
                  key={item.id}
                  className={`mobile-link ${activeSection === item.id ? 'active' : ''}`}
                  onClick={() => scrollToSection(item.id)}
                >
                  <span>{item.icon}</span>
                  {item.label}
                </button>
              ))}
            </motion.nav>
          </>
        )}
      </AnimatePresence>
    </>
  );
}

const css = `
:root {
  --bg: rgba(0,0,0,0.85);
  --green: #00ff41;
  --cyan: #00ffff;
}

.header {
  position: fixed;
  inset: 0 0 auto 0;
  z-index: 1000;
  padding: 1.25rem 0;
  background: var(--bg);
  backdrop-filter: blur(12px);
  transition: all .3s ease;
}

.header.scrolled {
  box-shadow: 0 10px 40px rgba(0,0,0,.6);
}

.header-container {
  max-width: 1400px;
  margin: auto;
  padding: 0 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.logo {
  font-family: monospace;
  font-size: 1.4rem;
  font-weight: 800;
}

.logo-bracket { color: var(--green); }

.nav-desktop {
  display: flex;
  gap: 2rem;
}

.nav-link {
  position: relative;
  color: #aaa;
  font-size: .85rem;
  padding: .5rem .75rem;
}

.nav-link.active { color: var(--green); }

.nav-indicator {
  position: absolute;
  left: 0; right: 0; bottom: -6px;
  height: 2px;
  background: linear-gradient(90deg,var(--green),var(--cyan));
  box-shadow: 0 0 12px var(--green);
}

.menu-toggle {
  display: none;
}

.progress-bar {
  height: 3px;
  background: linear-gradient(90deg,var(--green),var(--cyan));
  transform-origin: 0;
}

.mobile-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.7);
  backdrop-filter: blur(6px);
}

.mobile-menu {
  position: fixed;
  right: 0; top: 0; bottom: 0;
  width: 280px;
  background: #0a0a0a;
  padding: 2rem;
}

.mobile-link {
  display: flex;
  gap: 1rem;
  padding: 1rem;
  background: none;
  border: none;
  color: #aaa;
  font-size: 1rem;
}

.mobile-link.active { color: var(--green); }

@media (max-width: 900px) {
  .nav-desktop { display: none; }
  .menu-toggle {
    display: flex;
    flex-direction: column;
    gap: 5px;
    background: none;
    border: none;
  }
  .menu-toggle span {
    width: 22px;
    height: 2px;
    background: var(--green);
  }
}
`;
